/**
 * Chip de statut d'un post (affiché en haut-droite de la PostCard).
 *
 * Convention UX (cf plan UI/UX 2026-05) :
 *   - draft / pending    → pas de chip (état par défaut, inutile de surcharger).
 *   - scheduled          → chip violet "Programmé" + icône horloge.
 *   - published          → chip vert "Publié" + check.
 *   - failed             → chip rouge "Échec" + alerte.
 *
 * UX backlog finding 3.2 : ajout d'une icône préfixe pour différencier visuellement
 * Publié et Programmé (avant : taille + style identiques, confusion possible et
 * inaccessible pour les daltoniens). Saturation augmentée (0.12 → 0.22).
 *
 * Le composant retourne `null` pour les statuts neutres, donc on peut le
 * monter inconditionnellement côté caller sans branchement.
 */
import { Check, Clock, AlertTriangle } from 'lucide-react';
import { PostStatus } from '../../../utils/Constants';
import { useLanguage } from '../../../contexts/LanguageContext';

interface StatusChipProps {
  status?: PostStatus | string;
  /** Réduit la taille du chip si vrai (utile en sticky/header dense). */
  compact?: boolean;
}

export function StatusChip({ status, compact = false }: StatusChipProps) {
  const { t } = useLanguage();

  if (!status || status === PostStatus.DRAFT || status === PostStatus.PENDING) {
    return null;
  }

  const sizeCls = compact
    ? 'text-[9px] px-1.5 py-0.5 gap-1'
    : 'text-[10px] px-2 py-0.5 gap-1';

  const iconSize = compact ? 'w-2.5 h-2.5' : 'w-3 h-3';

  const baseStyle = {
    borderRadius: '999px',
    fontWeight: 600,
    lineHeight: 1.2,
    whiteSpace: 'nowrap' as const,
    display: 'inline-flex',
    alignItems: 'center',
  };

  if (status === PostStatus.SCHEDULED) {
    return (
      <span
        className={sizeCls}
        style={{
          ...baseStyle,
          // Saturation augmentée (0.12 → 0.22) — meilleur contraste sur fond clair.
          backgroundColor: 'rgba(85, 0, 255, 0.22)',
          color: 'var(--color-primary)',
        }}
      >
        <Clock className={iconSize} aria-hidden="true" />
        {t.posts.statusChipScheduled ?? 'Programmé'}
      </span>
    );
  }

  if (status === PostStatus.PUBLISHED) {
    return (
      <span
        className={sizeCls}
        style={{
          ...baseStyle,
          backgroundColor: 'rgba(34, 197, 94, 0.22)',
          color: 'var(--color-success)',
        }}
      >
        <Check className={iconSize} aria-hidden="true" />
        {t.posts.statusChipPublished ?? 'Publié'}
      </span>
    );
  }

  if (status === PostStatus.FAILED) {
    return (
      <span
        className={sizeCls}
        style={{
          ...baseStyle,
          backgroundColor: 'rgba(239, 68, 68, 0.22)',
          color: 'var(--color-danger)',
        }}
      >
        <AlertTriangle className={iconSize} aria-hidden="true" />
        {t.posts.statusChipFailed ?? 'Échec'}
      </span>
    );
  }

  return null;
}
